import type { AnySceneObjectT } from "tweenly"
import { measureInCanvas } from "./canvas.helpers"
import {
    DraggedEdges,
    guidesFor,
    NOT_SNAPPED,
    SNAP_THRESHOLD_PX,
    SnapDelta,
    SnapGuide,
    SnapRect,
    SnappedAxes,
    snapEdges,
    snapRect,
    unionRects,
} from "./snap.helpers"
import { collectObjectIds, ObjectsById } from "./wrapper.helpers"

/**
 * Snap to objects — the DOM half: measuring the canvas once per move or resize, and
 * publishing the guides for `SnapGuides` to draw. The geometry itself lives in snap.helpers.
 */

/** Same id the canvas element is rendered with, see Canvas.tsx. */
const CANVAS_ELEMENT_ID = "canvas"

export type SnapSession = {
    /**
     * Pull a drag delta (canvas units) onto the nearest object and publish the guides.
     *
     * @param lockedAxis the axis a shift-drag is restricted to, if any
     * @param bypass snapping is suspended for this move (ctrl held, as in Figma)
     */
    snap: (
        delta: SnapDelta,
        lockedAxis: "x" | "y" | undefined,
        bypass: boolean
    ) => { delta: SnapDelta; snapped: SnappedAxes }
    /** The drag is over — take the guides down. */
    end: () => void
}

/* ---------- guides store, read by SnapGuides through useSyncExternalStore ---------- */

const NO_GUIDES: SnapGuide[] = []
let currentGuides: SnapGuide[] = NO_GUIDES
const guideListeners = new Set<() => void>()

const sameGuides = (a: SnapGuide[], b: SnapGuide[]): boolean =>
    a.length === b.length &&
    a.every(
        (guide, index) =>
            guide.axis === b[index].axis &&
            guide.position === b[index].position &&
            guide.start === b[index].start &&
            guide.end === b[index].end &&
            guide.markers.length === b[index].markers.length &&
            guide.markers.every((marker, markerIndex) => marker === b[index].markers[markerIndex])
    )

/** Notifies only on a real change, so a drag that snaps nowhere re-renders nothing. */
const publishGuides = (guides: SnapGuide[]) => {
    const next = guides.length ? guides : NO_GUIDES
    if (sameGuides(currentGuides, next)) return
    currentGuides = next
    guideListeners.forEach((listener) => listener())
}

export const subscribeSnapGuides = (listener: () => void) => {
    guideListeners.add(listener)
    return () => {
        guideListeners.delete(listener)
    }
}

export const getSnapGuides = (): SnapGuide[] => currentGuides

/* ---------- measuring ---------- */

const isHiddenInStore = (objectId: string, objectsById: ObjectsById): boolean => {
    let current: AnySceneObjectT | undefined = objectsById[objectId]
    while (current) {
        if (current.isHidden) return true
        current = current.parentId ? objectsById[current.parentId] : undefined
    }
    return false
}

type Measurement = {
    /** the moving objects' bounding box where the gesture started */
    start: SnapRect
    targets: SnapRect[]
    /** `SNAP_THRESHOLD_PX` in canvas units */
    threshold: number
}

/**
 * Measure a gesture on `movingIds` against everything else on the canvas.
 *
 * Everything is measured from the DOM, once, before the gesture changes anything: what the
 * user lines up against is what is painted (flex children, auto-sized text and the current
 * animation frame included), not what the styles say. The moving objects carry their
 * descendants with them, so neither can be a target. The canvas itself is one — like the
 * parent frame in Figma — so the selection snaps to its edges and centre as well.
 *
 * @returns null when there is nothing to measure (no canvas, nothing rendered)
 */
const measureSnapTargets = (movingIds: string[], objectsById: ObjectsById): Measurement | null => {
    const canvasElement = document.getElementById(CANVAS_ELEMENT_ID)
    if (!canvasElement || !canvasElement.offsetWidth) return null

    const canvasRect = canvasElement.getBoundingClientRect()
    // client px per canvas unit — fit × zoom, read off the DOM so it cannot disagree with it
    const scale = canvasRect.width / canvasElement.offsetWidth || 1

    const measure = (element: Element): SnapRect => measureInCanvas(element, canvasRect, scale)

    const start = unionRects(
        movingIds
            .map((id) => document.getElementById(id))
            .filter((element): element is HTMLElement => element !== null)
            .map(measure)
    )
    if (!start) return null

    const excluded = new Set<string>()
    movingIds.forEach((id) => collectObjectIds(id, objectsById).forEach((x) => excluded.add(x)))

    const targets: SnapRect[] = [
        { left: 0, top: 0, width: canvasElement.offsetWidth, height: canvasElement.offsetHeight },
    ]
    Object.keys(objectsById).forEach((id) => {
        if (excluded.has(id) || isHiddenInStore(id, objectsById)) return
        const element = document.getElementById(id)
        // also covers objects hidden by the timeline at the current frame
        if (!element || getComputedStyle(element).visibility === "hidden") return
        const rect = measure(element)
        if (rect.width > 0 || rect.height > 0) targets.push(rect)
    })

    return { start, targets, threshold: SNAP_THRESHOLD_PX / scale }
}

/** Start snapping a move of `movingIds` — see `measureSnapTargets`. */
export const createSnapSession = (
    movingIds: string[],
    objectsById: ObjectsById
): SnapSession | null => {
    const measurement = measureSnapTargets(movingIds, objectsById)
    if (!measurement) return null
    const { start, targets, threshold } = measurement

    return {
        snap: (delta, lockedAxis, bypass) => {
            if (bypass) {
                publishGuides(NO_GUIDES)
                return { delta, snapped: NOT_SNAPPED }
            }
            const result = snapRect(start, delta, targets, threshold, lockedAxis)
            publishGuides(result.guides)
            return { delta: result.delta, snapped: result.snapped }
        },
        end: () => publishGuides(NO_GUIDES),
    }
}

export type ResizeSnapSession = {
    /**
     * Pull the dragged edges onto the nearest object.
     *
     * @param edges the edges the handle drags, where the gesture started
     * @param bypass snapping is suspended for this move (ctrl held, as in Figma)
     */
    snap: (
        edges: DraggedEdges,
        delta: SnapDelta,
        bypass: boolean
    ) => { delta: SnapDelta; snapped: SnappedAxes }
    /**
     * Draw the guides for the box the resize produced (absolute canvas units).
     *
     * Separate from `snap`, because only the box — after the handle has anchored, mirrored
     * and kept proportions — tells which lines really ended up aligned.
     */
    showGuides: (box: SnapRect) => void
    /** The resize is over — take the guides down. */
    end: () => void
}

/** Start snapping a resize of `resizedIds` — see `measureSnapTargets`. */
export const createResizeSnapSession = (
    resizedIds: string[],
    objectsById: ObjectsById
): ResizeSnapSession | null => {
    const measurement = measureSnapTargets(resizedIds, objectsById)
    if (!measurement) return null
    const { targets, threshold } = measurement
    let bypassed = false

    return {
        snap: (edges, delta, bypass) => {
            bypassed = bypass
            return bypass
                ? { delta, snapped: NOT_SNAPPED }
                : snapEdges(edges, delta, targets, threshold)
        },
        showGuides: (box) => publishGuides(bypassed ? NO_GUIDES : guidesFor(box, targets)),
        end: () => publishGuides(NO_GUIDES),
    }
}
